import { cn, type PolymorphProps } from "logic/utils";
import type { ElementType } from "react";


import { BADGE_VARIANTS, type BadgeVariant } from "./variants";

/**
 * Variants supported by {@link IconBadge}. Excludes `tertiary-mono` — that's a
 * text-only variant in the design system (mono typography on a tertiary bg).
 */
type IconBadgeVariant = Exclude<BadgeVariant, "tertiary-mono">;

interface IconBadgeExtraProps {
  /**
   * Color variant. Same palette as {@link Badge} minus the text-only
   * `tertiary-mono` variant.
   * @default "primary"
   */
  variant?: IconBadgeVariant;
  /**
   * Container size — `md` = 28px, `lg` = 36px. Matches Figma's `size` variant.
   * @default "lg"
   */
  size?: "md" | "lg";
}

/**
 * Polymorphic props for `<IconBadge>`. Defaults to `<span>`; pass `as="button"`
 * etc. to change the rendered element.
 */
export type IconBadgeProps<T extends ElementType> = PolymorphProps<T, IconBadgeExtraProps>;

const SIZE_OUTER: Record<NonNullable<IconBadgeExtraProps["size"]>, string> = {
  md: "size-7", // 28px (--container/xs)
  lg: "size-9", // 36px (--container/md)
};

/**
 * Square icon-only badge — a chunkier tinted tile holding a single icon, used
 * for hardware/source markers, asset chips, and feature affordances. Mirrors
 * Figma `Icon Badge` (design-system node `454:35443`).
 *
 * Use {@link Badge} for text-bearing pills.
 *
 * The child icon inherits the variant label color via `currentColor`, so pass
 * icons that respect `stroke="currentColor"` / `fill="currentColor"`.
 *
 * @example Default 36px primary
 * ```tsx
 * <IconBadge>
 *   <ServerStackIcon className="size-5" />
 * </IconBadge>
 * ```
 *
 * @example Compact 28px warning
 * ```tsx
 * <IconBadge size="md" variant="warning">
 *   <ClockIcon className="size-4" />
 * </IconBadge>
 * ```
 *
 * @example Clickable (polymorphic as button)
 * ```tsx
 * <IconBadge as="button" type="button" variant="accent-2" onClick={...}>
 *   <FilterIcon className="size-5" />
 * </IconBadge>
 * ```
 *
 * @see {@link Badge} for the text-bearing variant.
 * @see Figma — https://www.figma.com/design/hCI9wopUpt49KQ7bWrDGuB/USD.AI-%7C-Design-System?node-id=454-35443
 */
export function IconBadge<T extends ElementType = "span">(props: IconBadgeProps<T>) {
  const { as, variant = "primary", size = "lg", className, children, componentRef, ...rest } = props;

  const Component = as ?? "span";
  const tokens = BADGE_VARIANTS[variant];

  return (
    <Component
      ref={componentRef}
      {...rest}
      className={cn("inline-flex items-center justify-center rounded-[4px]", tokens.bg, SIZE_OUTER[size], className)}
    >
      <span className={cn("flex size-full items-center justify-center gap-1 rounded-[6px] p-2", tokens.stateLayer)}>
        <span className={cn("flex size-5 shrink-0 items-center justify-center", tokens.label, tokens.labelOpacity)}>
          {children}
        </span>
      </span>
    </Component>
  );
}
